import Scope from '@core/managers/Scope';
import { TEventInformation } from '@core/events';
import Form from '@core/managers/Form';

import {
  IObservable,
  TEvents,
  TEventsKeys,
  TObservable,
  TObserverData,
  TSchema,
  TScope,
} from '@core/types';
import {
  DEFAULT_FORM_ID,
  EEVents,
  EXTRACT_CORE_NAMESPACE,
  EXTRACT_EVENT_NAMESPACE,
} from '@core/constants';
import {
  EFlowLogging,
  TEventPublishPayload,
  TLoggingEvent,
} from '@core/events/events.types';
import { ObserverError } from '@core/events/ObserverError';

class Factory {
  workers: Record<string, Form> = {};

  constructor() {
    this.getFormInstance = this.getFormInstance.bind(this);
    this.getGroupFormsIds = this.getGroupFormsIds.bind(this);
  }

  getGroupFormsIds(group: string) {
    return Object.keys(this.workers || {}).filter(
      (key) => this.workers[key].group === group,
      [],
    );
  }

  getFormInstance(
    namespace = DEFAULT_FORM_ID,
    opts?: {
      schema?: TSchema;
      initialScope?: TScope;
      initialValues?: Record<string, any>;
      newInstance?: boolean;
      group?: string;
    },
  ) {
    if (this.workers[namespace] && !opts?.newInstance) {
      return this.workers[namespace];
    }

    const observer = new Observer(
      namespace,
      opts?.initialScope?.configs?.enableLogging,
    );
    const scope = new Scope(observer);

    scope.initialScope = { ...opts?.initialScope, ...opts?.schema?.iVars };
    this.workers[namespace] = new Form(
      namespace,
      observer,
      scope,
      opts?.schema,
      {
        initialValues: opts?.initialValues || {},
      },
      opts?.group,
    );
    return this.workers[namespace];
  }
}

class Observer implements IObservable {
  regexBasedEvents: Record<string, { regex: RegExp; handlers: TObservable[] }> =
    {};
  events: TEvents = {};
  history: { [key in EEVents]?: string } = {};
  namespace: string;
  enableLogging: boolean;
  constructor(namespace: string, enableLogging = false) {
    this.namespace = namespace;
    this.enableLogging = enableLogging;
  }

  #buildEventPayload = ({
    event,
    data,
    payload,
  }: TObserverData<unknown>): TEventInformation<unknown> => {
    const eventFieldName = EXTRACT_EVENT_NAMESPACE(event);
    const form = getFormInstance(this.namespace, data?.opts);
    const field = form.fields[eventFieldName];
    const coreEvent = EXTRACT_CORE_NAMESPACE(event);

    return {
      eventReducedSchema: field && field.eventReducedSchema(coreEvent),
      formEventDirectives: form && form.eventReducedSchema(coreEvent),
      namespace: this.namespace,
      coreEvent,
      form,
      field,
      event,
      data,
      payload,
    };
  };

  runForRegexBasedEvent(eventName, cb: (event: TEventsKeys) => void) {
    let isMatchEvent = false;
    for (const event of Object.keys(this.events)) {
      isMatchEvent = (eventName as RegExp).test(event);
      isMatchEvent && cb(event as TEventsKeys);
    }

    return isMatchEvent;
  }

  handleRegexSubscription(eventName: any, handler: TObservable) {
    const foundEvent = this.runForRegexBasedEvent(eventName, (event) =>
      this.subscribe(event as TEventsKeys, handler),
    );

    if (!foundEvent) {
      this.regexBasedEvents[eventName] = {
        regex: eventName,
        handlers: Array.isArray(this.events[eventName])
          ? [...this.events[eventName], handler]
          : [handler],
      };
    }
  }

  /**
   * This function lets you subscribe to a given event and register one callback to be called when someone published in it
   *
   * The callback you redister will, return you the published data and one function to unregister your callback from that event
   *
   */
  subscribe(eventName: TEventsKeys, handler: TObservable) {
    if (typeof eventName === 'object') {
      this.handleRegexSubscription(eventName, handler);
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      return () => {};
    }

    this.events = {
      ...this.events,
      [eventName]: Array.isArray(this.events[eventName])
        ? [...this.events[eventName], handler]
        : [handler],
    };

    return () => {
      this.unsubscribe(eventName, handler);
    };
  }

  unsubscribe(eventName: TEventsKeys, handler: TObservable) {
    this.events = {
      ...this.events,
      [eventName]: (this.events as TEvents)[eventName].filter(
        (eventFn) => eventFn !== handler,
      ),
    };
  }

  isAsyncFunction(fn: (...data: any) => any) {
    const string = fn.toString().trim();
    return !!(
      fn[Symbol.toStringTag] === 'AsyncFunction' ||
      fn.constructor.name == 'AsyncFunction' ||
      string.match(/^async /) ||
      string.match(/return _ref[^\\.]*\.apply/) ||
      fn instanceof Promise
    );
  }

  /**
   * Allows to publish data to a given event name
   *
   * Will iterate the subscriptions and call their handlers.
   *
   * When calling the handler, will also inject the unsubscribe function
   *
   * This methods also accepts one regex and will find the matchin events and
   * publish in them
   *
   */
  async publish(eventName: TEventsKeys, data?: TEventPublishPayload) {
    if (data?.checksum && this.history[eventName] === data?.checksum) return;

    this.history[eventName] = data?.checksum;
    if (typeof eventName === 'object') {
      this.runForRegexBasedEvent(eventName, (event) =>
        this.publish(event as TEventsKeys, data),
      );
      return;
    }

    for (const regexEvent of Object.keys(this.regexBasedEvents)) {
      const isMatchEvent = new RegExp(
        this.regexBasedEvents[regexEvent].regex,
      ).test(eventName);
      if (isMatchEvent) {
        await this.publishForEvents(eventName, data, {
          [eventName]: this.regexBasedEvents[regexEvent].handlers,
        });
      }
    }

    return this.publishForEvents(eventName, data, this.events);
  }

  async publishForEvents(eventName: TEventsKeys, data = {}, events: TEvents) {
    if (!events[eventName]) return {};
    let lastCallbackResult = {};

    for (const fn of events[eventName]) {
      try {
        if (!fn) return;
        const payload: TObserverData<unknown> = this.#buildEventPayload({
          data: {
            ...data,
            ...lastCallbackResult,
          },
          payload: data,
          event: eventName,
          namespace: this.namespace,
        });
        const isAsync = this.isAsyncFunction(fn);

        const handlerRes = isAsync
          ? await fn(payload, () => this.unsubscribe(eventName, fn))
          : fn(payload, () => this.unsubscribe(eventName, fn));

        lastCallbackResult = {
          ...lastCallbackResult,
          ...handlerRes,
        };
      } catch (e) {
        if (e instanceof ObserverError && e.breaksObservingChain) {
          this.logError(EFlowLogging.OBSERVER, eventName, 'publish', e);
          return {};
        }
        throw e;
      }
    }

    return {
      ...lastCallbackResult,
      ...getFormInstance(this.namespace).formData,
    };
  }

  logError(
    flow: Pick<TLoggingEvent, 'flow'>['flow'],
    event: string,
    method: string,
    error: unknown,
  ) {
    if (!this.enableLogging) return;
    event !== EEVents.LOG &&
      this.publish(EEVents.LOG, {
        level: 'error',
        data: {
          event,
          error,
        },
        action: method,
        flow,
      });
  }

  logInfo(
    flow: Pick<TLoggingEvent, 'flow'>['flow'],
    event: string,
    method: string,
    extraData?: any,
  ) {
    if (!this.enableLogging) return;
    event !== EEVents.LOG &&
      this.publish(EEVents.LOG, {
        level: 'info',
        data: {
          event,
          ...extraData,
        },
        action: method,
        flow,
      });
  }
}

export const { getFormInstance, getGroupFormsIds } = new Factory();
export { Observer };
